/**
 * Server CSV export: sort field + direction (Phone limit All).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { ServerCsvExportSortConfig } from './phoneServerExportSortOptions';

const FIELD_SELECT_CLASS =
  'h-9 w-full min-w-0 rounded-md border border-input bg-background px-3 text-sm shadow-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

interface CsvExportServerSortSectionProps {
  config: ServerCsvExportSortConfig;
  field: string;
  dir: 'asc' | 'desc';
  disabled: boolean;
  onFieldChange: (v: string) => void;
  onDirChange: (v: 'asc' | 'desc') => void;
  /** First select option; server CSV uses streamed Voluum order when empty. */
  defaultOptionLabel?: string;
  /** Extra hint under Row order (e.g. full server export perf). */
  sortPerformanceHint?: string;
}

export const CsvExportServerSortSection: FC<CsvExportServerSortSectionProps> = ({
  config,
  field,
  dir,
  disabled,
  onFieldChange,
  onDirChange,
  defaultOptionLabel = 'Default (same as table)',
  sortPerformanceHint,
}) => {
  const dirDisabled = disabled || field === '';

  return (
    <section className="space-y-2.5 rounded-md border border-border/30 bg-muted/5 p-2.5">
      <div>
        <h4 className="text-xs font-semibold text-muted-foreground">Row order</h4>
        <p className="text-[11px] leading-snug text-muted-foreground">
          Applies to the downloaded file only; the table view is unchanged.
          {sortPerformanceHint ? ` ${sortPerformanceHint}` : ''}
        </p>
      </div>
      <div className="flex w-full min-w-0 flex-col gap-2.5">
        <div className="min-w-0 space-y-1">
          <label htmlFor="csv-export-sort-field" className="text-xs font-medium text-foreground">
            Sort by
          </label>
          <select
            id="csv-export-sort-field"
            name="csvExportSortField"
            className={FIELD_SELECT_CLASS}
            value={field}
            disabled={disabled}
            onChange={(e) => onFieldChange(e.target.value)}
            aria-label="CSV export sort field"
          >
            <option value="">{defaultOptionLabel}</option>
            {config.options.map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div
          className="min-w-0 space-y-1"
          role="group"
          aria-labelledby="csv-export-sort-direction-label"
        >
          <span
            id="csv-export-sort-direction-label"
            className="block text-xs font-medium text-foreground"
          >
            Direction
          </span>
          <div className="flex h-9 w-full min-w-0 rounded-md border border-input bg-muted/30 p-0.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={dirDisabled}
              className={cn(
                'h-full flex-1 rounded-sm px-2 text-xs font-medium',
                dir === 'desc' && 'bg-background text-foreground shadow-sm',
              )}
              aria-label="Descending"
              onClick={() => onDirChange('desc')}
            >
              Desc
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={dirDisabled}
              className={cn(
                'h-full flex-1 rounded-sm px-2 text-xs font-medium',
                dir === 'asc' && 'bg-background text-foreground shadow-sm',
              )}
              aria-label="Ascending"
              onClick={() => onDirChange('asc')}
            >
              Asc
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
